import { formatDropdownOptions } from 'common/utils';
import { ApprovalSetupFlowCommonData_Query } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { ApprovalTestResultCreateVariables } from '../../__generated__/ApprovalTestResultCreate';
import { FlowApproval_Query } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';
import { TestCaseValues } from '../types';
import { ApprovalTestResultUpdateVariables } from '../../__generated__/ApprovalTestResultUpdate';
import { ApprovalTestResult_approvalTestResult } from '../../__generated__/ApprovalTestResult';
import { ApprovalTestResultPatch } from 'common/types/globalTypes';

export const getAddTestCaseVariables = (
  values: TestCaseValues,
  approvalDetailsData: FlowApproval_Query | undefined,
  testGroupId: string | undefined
): ApprovalTestResultCreateVariables => {
  const payloadValues: ApprovalTestResultCreateVariables = {
    id: approvalDetailsData?.approval?.id!,
    input: {
      approvalId: approvalDetailsData?.approval?.id!,
      approvalTestGroupId: testGroupId,
      approvalTestResult: {
        ...values,
        comment: values.comment!,
        elapsedDays: Number(values.elapsedDays?.toString()),
      },
      clientMutationId: undefined,
    },
  };

  return payloadValues;
};

const SKIP_KEYS: (keyof ApprovalTestResult_approvalTestResult)[] = [
  'id',
  '_rowTimestamp',
  '_fullComment',
  '_isCustomTest',
  '_isUpdatable',
  'approvalTestGroupId',
  'approvalTestItemId',
  'approvalGroupName',
  'businessUnit',
  'department',
  'currency',
  'approvalPhase',
  'activeProfile',
  'userGroup',
  'workgroup',
  'approverTransactionType',
  'purchaseOrderType',
  'tag',
  'approvalLevelValue',
  'userRankType',
  'secureRowLevel',
  'additionalBusinessUnit',
  'additionalDepartment',
  'isGlobalTest',
] as const;

export const getUpdateTestCaseVariables = (
  values: TestCaseValues,
  approvalTestData: ApprovalTestResult_approvalTestResult,
  testGroupId: string | undefined,
  changeToCustomTest: boolean
): ApprovalTestResultUpdateVariables => {
  const approvalTestResultPatch = (
    Object.keys(values) as (keyof ApprovalTestResultPatch)[]
  ).reduce((acc, key) => {
    if (
      !SKIP_KEYS.includes(key as keyof ApprovalTestResult_approvalTestResult) &&
      key in approvalTestData &&
      values[key] !== approvalTestData[key]
    ) {
      acc[key] = values[key] as any; // will explain below
    }
    return acc;
  }, {} as ApprovalTestResultPatch);
  return {
    input: {
      id: approvalTestData.id,
      approvalTestGroupId: testGroupId,
      isTestGroupRemoval: changeToCustomTest,
      rowTimestamp: approvalTestData._rowTimestamp!,
      approvalTestResultPatch,
    },
  };
};

export const useDropdownOptions = (
  approvalTypes: ApprovalSetupFlowCommonData_Query | undefined
) => {
  const currencies =
    formatDropdownOptions(approvalTypes?.companyCurrencies?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: false,
    }) || [];

  const statusTypes =
    formatDropdownOptions(approvalTypes?.statusTypes?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.statusType!,
      includeAll: false,
    }) || [];

  const activeProfiles =
    formatDropdownOptions(approvalTypes?.activeProfiles?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: false,
    }) || [];

  const additionalBusUnit =
    formatDropdownOptions(approvalTypes?.businessUnits?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: false,
    }) || [];

  const approvalPhases =
    formatDropdownOptions(approvalTypes?.approvalPhases?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.phaseName || '',
      includeAll: false,
    }) || [];

  const approvalLevelValues =
    formatDropdownOptions(approvalTypes?.approvalLevelValues?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: false,
    }) || [];

  const corporateWorkgroup =
    formatDropdownOptions(approvalTypes?.corporateWorkgroups?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name || '',
      includeAll: false,
    }) || [];

  const conversionOptions =
    formatDropdownOptions(approvalTypes?.exchangeRateTypes?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.phaseName || '',
      includeAll: false,
    }) || [];

  const occupations =
    formatDropdownOptions(approvalTypes?.userOccupationTitles?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.userOccupationTitle!,
      includeAll: false,
    }) || [];

  const ranks =
    formatDropdownOptions(approvalTypes?.userRankTypes?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.userRank!,
      includeAll: false,
    }) || [];

  const companies =
    formatDropdownOptions(approvalTypes?.companies?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: false,
    }) || [];

  const users =
    formatDropdownOptions(approvalTypes?.users?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: false,
    }) || [];

  const userGroups =
    formatDropdownOptions(approvalTypes?.userGroups?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: false,
    }) || [];

  const purchaseOrder =
    formatDropdownOptions(approvalTypes?.companyPurchaseOrderTypes?.nodes, {
      getKey: (item) => item.purchaseOrderTypeId!,
      getText: (item) => item.purchaseOrderType!,
      includeAll: false,
    }) || [];

  const departments =
    formatDropdownOptions(approvalTypes?.departments?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: false,
    }) || [];

  const departmentTypes =
    formatDropdownOptions(approvalTypes?.departmentTypes?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.departmentType,
      includeAll: false,
    }) || [];

  const departmentGroups =
    formatDropdownOptions(approvalTypes?.departmentGroups?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name,
      includeAll: false,
    }) || [];

  const tags =
    formatDropdownOptions(approvalTypes?.tags?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.badgeName,
      includeAll: false,
    }) || [];

  const rowSecurity =
    formatDropdownOptions(approvalTypes?.secureRowLevels?.nodes, {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: false,
    }) || [];

  return {
    currencies,
    additionalBusUnit,
    conversionOptions,
    occupations,
    ranks,
    companies,
    userGroups,
    users,
    approvalLevelValues,
    purchaseOrder,
    departments,
    departmentTypes,
    departmentGroups,
    tags,
    rowSecurity,
    approvalPhases,
    corporateWorkgroup,
    statusTypes,
    activeProfiles,
  };
};
